import { Circ, Elastic, Expo, Sine, TimelineLite, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import { Button, OutlinedButton } from "../gameobjects/Button";
import {AbstractGameState} from "./AbstractGameState";

import { Application } from "../Application";
import {TrashCollectIntroLayout} from "../layout/TrashCollectIntroLayout";
import { CaptionManager } from "../managers/CaptionManager";
import { LightsOutElmoBathroomScreen } from "./LightsOutElmoBathroomScreen";
import { LightsOutElmoBedroomScreen } from "./LightsOutElmoBedroomScreen";
import { LightsOutElmoKitchenScreen } from "./LightsOutElmoKitchenScreen";
import { LightsOutElmoLivingRoomScreen } from "./LightsOutElmoLivingRoomScreen";
import { LightsOutIntroScreen } from "./LightsOutIntroScreen";
import { PlanterCustomizeIntroScreen } from "./PlanterCustomizeIntroScreen";
import { TrashCollectIntroScreen } from "./TrashCollectIntroScreen";

import { Grover } from "../gameobjects/Grover";
import {MainMenuLayout} from "../layout/MainMenuLayout";

import { random } from "html-living-framework/lib/Utils/ArrayUtils";
import { isMobile } from "html-living-framework/lib/Utils/PlatformUtils";
import { CaptionToken } from "../data/CaptionToken";
import LightsOutGameModel from "../data/LightsOutGameModel";
import * as PlanterGameModel from "../data/PlanterGameModel";
import TrashGameModel from "../data/TrashGameModel";
import * as VO from "../data/VO";
import { randomExhaust } from "../data/VO";
import { AbstractBadgePopup } from "../popups/AbstractBadgePopup";

const clickVO = isMobile() ? VO.MM_PROMPT01 : VO.MM_PROMPT02;
const idleVOs = [VO.MM_TO1, [clickVO, VO.MM_TO2]];
const groverReactVO = [VO.MM08, VO.MM09];
HLF.ArrayUtils.shuffle(groverReactVO);

export class MainMenu extends AbstractGameState {

    public static readonly NAME = "MainMenu";

    protected static firstTime = true;

    public readonly bg = this.addChild(new Sprite());
    public readonly grover = this.addChild(new Grover());

    public readonly oscarButton = this.addChild(new OutlinedButton());
    public readonly abbyButton = this.addChild(new OutlinedButton());
    public readonly elmoButton = this.addChild(new OutlinedButton());
    protected analyticsScreenName: string = "3 Menu Screen";
    protected analyticsRoundName = undefined;
    public init(pSize: PIXI.Point, pData: {}) {
        MainMenuLayout.call(this);
        this.oscarButton.clickSfx = "buttonclick";
        this.abbyButton.clickSfx = "buttonclick";
        this.elmoButton.clickSfx = "buttonclick";

        const menuStartTime = new Date().valueOf();
        this.trackEvent("gameComponentStart", {label: "Choice_Menu"});

        this.oscarButton.onClick.add(() => {
            this.trackEvent("gameMenuClick",
                { label: `MenuButton_Trash Blast_ScreenName_${this.analyticsScreenName}` },
            );
            if (this.idleStartTime !== undefined) {
                const idleEndTime = new Date().valueOf();
                const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                this.trackEvent("gameTOReEngage", {label: `ScreenName_${this.analyticsScreenName}`, value: idleDuration});
                this.idleStartTime = undefined;
            }
            this.trackEvent("gameComponentComplete", {
                label: "Choice_Menu",
                value: (new Date().valueOf() - menuStartTime) / 1000,
            });

            this.abbyButton.interactive = false;
            this.elmoButton.interactive = false;
            this.oscarButton.interactive = false;
            this.oscarButton.scaleTo(1, 1, Elastic.easeOut);
            this.elmoButton.scaleTo(1, 1, Elastic.easeOut);
            this.abbyButton.scaleTo(1, 1, Elastic.easeOut);
            this.oscarButton.outlineFilter.enabled = false;
            this.elmoButton.outlineFilter.enabled = false;
            this.abbyButton.outlineFilter.enabled = false;
            const alreadyPlayedTrashBlast = TrashCollectIntroScreen.visited;
            Application.instance.voiceover.playVO(VO.MM_ACTIVITY01, (didPlayVO: boolean) => {
                const delay = didPlayVO ? 0 : 1;
                TweenLite.delayedCall(delay, () => {
                    if (!alreadyPlayedTrashBlast) {
                        this.gotoNextState(TrashCollectIntroScreen.NAME);
                    }
                    else {
                        this.gotoNextState("TrashCollectScreen", {fromMainMenu: true});
                    }
                });
            });
        });
        this.abbyButton.onClick.add(() => {
            this.trackEvent("gameMenuClick",
                { label: `MenuButton_Planter Party_ScreenName_${this.analyticsScreenName}` },
            );
            if (this.idleStartTime !== undefined) {
                const idleEndTime = new Date().valueOf();
                const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                this.trackEvent("gameTOReEngage", {label: `ScreenName_${this.analyticsScreenName}`, value: idleDuration});
                this.idleStartTime = undefined;
            }
            this.trackEvent("gameComponentComplete", {
                label: "Choice_Menu",
                value: (new Date().valueOf() - menuStartTime) / 1000,
            });
            this.abbyButton.interactive = false;
            this.elmoButton.interactive = false;
            this.oscarButton.interactive = false;
            this.oscarButton.scaleTo(1, 1, Elastic.easeOut);
            this.elmoButton.scaleTo(1, 1, Elastic.easeOut);
            this.abbyButton.scaleTo(1, 1, Elastic.easeOut);
            this.oscarButton.outlineFilter.enabled = false;
            this.elmoButton.outlineFilter.enabled = false;
            this.abbyButton.outlineFilter.enabled = false;
            const alreadyPlayedPlanterParty = PlanterCustomizeIntroScreen.visited;
            Application.instance.voiceover.playVO(VO.MM_ACTIVITY03,  (didPlayVO: boolean) => {
                const delay = didPlayVO ? 0 : 1;
                TweenLite.delayedCall(delay, () => {
                    if (!alreadyPlayedPlanterParty) {
                        this.gotoNextState(PlanterCustomizeIntroScreen.NAME);
                    }
                    else {
                        this.gotoNextState("PlanterCustomizeScreen", {fromMainMenu: true});
                    }
                });
            });
        });
        this.elmoButton.onClick.add(() => {
            this.trackEvent("gameMenuClick", {label: `MenuButton_Lights Out_ScreenName_${this.analyticsScreenName}`});
            if (this.idleStartTime !== undefined) {
                const idleEndTime = new Date().valueOf();
                const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                this.trackEvent("gameTOReEngage", {label: `ScreenName_${this.analyticsScreenName}`, value: idleDuration});
                this.idleStartTime = undefined;
            }
            this.trackEvent("gameComponentComplete", {
                label: "Choice_Menu",
                value: (new Date().valueOf() - menuStartTime) / 1000,
            });
            this.abbyButton.interactive = false;
            this.elmoButton.interactive = false;
            this.oscarButton.interactive = false;
            this.oscarButton.scaleTo(1, 1, Elastic.easeOut);
            this.elmoButton.scaleTo(1, 1, Elastic.easeOut);
            this.abbyButton.scaleTo(1, 1, Elastic.easeOut);
            this.oscarButton.outlineFilter.enabled = false;
            this.elmoButton.outlineFilter.enabled = false;
            this.abbyButton.outlineFilter.enabled = false;
            const alreadyPlayedLightsOut = LightsOutIntroScreen.visited;
            LightsOutGameModel.reset();
            Application.instance.voiceover.playVO(VO.MM_ACTIVITY02,  (didPlayVO: boolean) => {
                const delay = didPlayVO ? 0 : 1;
                TweenLite.delayedCall(delay, () => {
                    if (!alreadyPlayedLightsOut) {
                        this.gotoNextState(LightsOutIntroScreen.NAME);
                    }
                    else {
                        this.gotoNextState(random([LightsOutElmoBathroomScreen.NAME, LightsOutElmoLivingRoomScreen.NAME]), {fromMainMenu: true});
                    }
                });
            });
        });

        this.idleTimeline.pause();
        this.idleTimeline.call(() => {
            console.log("idle");
            this.trackEvent("gameTimeoutPlay", {label: `ScreenName_${this.analyticsScreenName}`});
            if (this.idleStartTime === undefined) {
                this.idleStartTime = new Date().valueOf();
            }
            this.oscarButton.scaleTo(1.15, 0.15, Sine.easeInOut).delay(0.5);
            this.elmoButton.scaleTo(1.15, 0.15, Sine.easeInOut).delay(0.5);
            this.abbyButton.scaleTo(1.15, 0.15, Sine.easeInOut).delay(0.5);
            this.oscarButton.outlineFilter.enabled = true;
            this.elmoButton.outlineFilter.enabled = true;
            this.abbyButton.outlineFilter.enabled = true;

            const voToPlay = idleVOs.shift()!;
            idleVOs.push(voToPlay);
            HLF.Application.instance.voiceover.playVO(voToPlay, {
                mode: HLF.PlayMode.New,
                callback: () => {
                    this.idleTimeline?.restart(true);
                    this.oscarButton.scaleTo(1, 1, Elastic.easeOut);
                    this.elmoButton.scaleTo(1, 1, Elastic.easeOut);
                    this.abbyButton.scaleTo(1, 1, Elastic.easeOut);
                    this.oscarButton.outlineFilter.enabled = false;
                    this.elmoButton.outlineFilter.enabled = false;
                    this.abbyButton.outlineFilter.enabled = false;
                },
                skipCC: true,
                priority: 0,
            });
            PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(voToPlay instanceof Array ? voToPlay[1] : voToPlay, 0, {
                prompt: clickVO,
            }));
        });

        super.init(pSize);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
        const bgm = HLF.Application.instance.audio.getAudioTrack("main_menu_loop", HLF.AudioCategory.BGM) ?? HLF.Application.instance.audio.createAudioTrack("main_menu_loop", HLF.AudioCategory.BGM);
        if (bgm.isPlaying() === false) {
            bgm.setLooped(true);
            bgm.play();
        }
        const addHoverVOs = () => {
            this.oscarButton.hoverVO = VO.MM_ACTIVITY01;
            this.abbyButton.hoverVO = VO.MM_ACTIVITY03;
            this.elmoButton.hoverVO = VO.MM_ACTIVITY02;

            this.grover.interactive = true;
            this.grover.buttonMode = true;
            this.grover.addListener(HLF.Input.Events.POINTER_TAP, () => {
                this.idleTimeline?.restart(true);

                this.trackEvent("gameInteraction", {label: `CharacterName_Grover_ScreenName_${this.analyticsScreenName}`});

                if (this.idleStartTime !== undefined) {
                    const idleEndTime = new Date().valueOf();
                    const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                    this.trackEvent("gameTOReEngage", {label: `ScreenName_${this.analyticsScreenName}`, value: idleDuration});
                    this.idleStartTime = undefined;
                }

                const voToPlay = groverReactVO[0];

                // cycle vo only if it actually played
                const onVoDidPlay = (_pTopic: string, _pData: string) => {
                    this.grover.Wave();
                    if (_pData === voToPlay) {
                        if (groverReactVO[0] === voToPlay) {
                            groverReactVO.push(groverReactVO.shift()!);
                        }
                    }
                    PubSub.unsubscribe(token);
                };

                const token = PubSub.subscribe(HLF.VOICEOVER_STARTED, onVoDidPlay);

                // NB: the callback never fires if the VO is interrupted, which is why we do the pubsub business
                HLF.Application.instance.voiceover.playVO(voToPlay!, HLF.PlayMode.New, (pDidPlay) => {
                    if (pDidPlay) {
                        // scale buttons down if idle VO is interrupted
                        this.oscarButton.scaleTo(1, 1, Elastic.easeOut);
                        this.elmoButton.scaleTo(1, 1, Elastic.easeOut);
                        this.abbyButton.scaleTo(1, 1, Elastic.easeOut);
                        this.oscarButton.outlineFilter.enabled = false;
                        this.elmoButton.outlineFilter.enabled = false;
                        this.abbyButton.outlineFilter.enabled = false;
                    }
                    PubSub.unsubscribe(token);
                    this.idleTimeline?.restart(true);
                });
            });
        };

        this.grover.Wave();

        if (MainMenu.firstTime) {
            MainMenu.firstTime = false;
            Application.instance.voiceover.playVO(VO.MM01, () => {
                this.oscarButton.scaleTo(1.15, 0.15, Sine.easeInOut).delay(0.5);
                this.oscarButton.outlineFilter.enabled = true;
                Application.instance.voiceover.playVO([0.5, VO.MM02], () => {
                    this.oscarButton.scaleTo(1, 1, Elastic.easeOut);
                    this.elmoButton.scaleTo(1.15, 0.15, Sine.easeInOut).delay(0.5);
                    this.oscarButton.outlineFilter.enabled = false;
                    this.elmoButton.outlineFilter.enabled = true;
                    Application.instance.voiceover.playVO([0.5, VO.MM03], () => {
                        this.elmoButton.scaleTo(1, 1, Elastic.easeOut);
                        this.abbyButton.scaleTo(1.15, 0.15, Sine.easeInOut).delay(0.5);
                        this.elmoButton.outlineFilter.enabled = false;
                        this.abbyButton.outlineFilter.enabled = true;
                        Application.instance.voiceover.playVO([0.5, VO.MM04], () => {
                            this.abbyButton.scaleTo(1, 1, Elastic.easeOut);
                            this.abbyButton.outlineFilter.enabled = false;
                            addHoverVOs();
                            this.idleTimeline?.restart(true);
                        });
                    });
                });
            });
        }
        else {
            Application.instance.voiceover.playVO(HLF.ArrayUtils.random([VO.MM05, VO.MM06, VO.MM07]), () => {
                addHoverVOs();
                this.idleTimeline?.restart(true);
            });
        }

    }

    public destroy() {
        super.destroy();
        this.idleTimeline?.kill();
        this.idleTimeline?.clear();
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set((pSize.x - 1024) / 2, 0);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.grover.update(pDeltaTime);
    }

}
